// O link dentro do texto (chat, comentário do feed, atendimento) — desenho da tela de 27/10/2026, §1.
// Um só para todos os lugares de texto puro: sublinhado sempre (é o que diz "clicável" sem depender de hover),
// branco na bolha própria e azul-céu (o par da #hashtag) em todo o resto.
//
// Fora: outra aba, sem passar o referrer. Da casa: `<Link>` do router, mesma janela, sem recarregar. Fora de um
// Router (as suítes antigas da bolha, do atendimento e do comentário renderizam sem ele) o link da casa cai num
// `<a href>` comum, mesma janela.
//
// "Da casa" é o host do SE_WEB E uma tela do app (`ehTelaDaCasa`). O que o mesmo host serve e não é tela (o arquivo
// assinado `/api/v1/storage/file/...`, `/health`, `/logo.png`) é arquivo, não rota do SPA: o router o levaria ao
// "não encontrado". Vai como link de fora, em outra aba (a pessoa não sai da conversa), com o `href` intacto.
//
// 🔴 `stopPropagation` SÓ no `click`: o `pointerdown` precisa subir, é dele que o menu do toque longo da bolha
// depende. (A cicatriz do portal flutuante, que manda parar o pointerdown, vale para quem renderiza em portal.)
import { Link, useInRouterContext } from "react-router-dom";
import type { MouseEvent } from "react";

import {
  caminhoDaCasa,
  ehHttpOuHttps,
  ehTelaDaCasa,
  type TomDoLink,
} from "@/lib/links/previaDoCartao";
import { cn } from "@/lib/utils";

interface Props {
  /** `texto` é o que a pessoa digitou (aparece inteiro); `href` é para onde o clique vai (`www.` ganha `https://`). */
  link: { texto: string; href: string };
  tom: TomDoLink;
}

const NAO_SOBE_O_CLIQUE = (e: MouseEvent) => e.stopPropagation();

const BASE =
  "underline underline-offset-2 decoration-from-font break-words rounded-sm focus-visible:outline-none focus-visible:ring-2";

const POR_TOM: Record<TomDoLink, string> = {
  propria: "text-white decoration-white/70 hover:decoration-white focus-visible:ring-white",
  neutro: "text-sky-700 dark:text-sky-300 hover:decoration-2 focus-visible:ring-ring",
};

export function LinkNoTexto({ link, tom }: Props) {
  const dentroDoRouter = useInRouterContext();
  // Só http e https viram <a>: qualquer outro esquema (`javascript:`, `data:`) fica como texto puro.
  if (!ehHttpOuHttps(link.href)) return <>{link.texto}</>;
  const casa = ehTelaDaCasa(link.href);
  const className = cn(BASE, POR_TOM[tom]);
  const comum = {
    className,
    onClick: NAO_SOBE_O_CLIQUE,
    "data-testid": "link-no-texto",
    "data-externo": casa ? "false" : "true",
  };

  if (casa && dentroDoRouter) {
    return (
      <Link to={caminhoDaCasa(link.href)} {...comum}>
        {link.texto}
      </Link>
    );
  }
  if (casa) {
    return (
      <a href={link.href} {...comum}>
        {link.texto}
      </a>
    );
  }
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      referrerPolicy="no-referrer"
      {...comum}
    >
      {link.texto}
    </a>
  );
}
